import jsPDF from 'jspdf';

/**
 * Generates and auto-downloads a PDF report card.
 * No print dialog — downloads directly as a file.
 */
export function printReportCard(reportData, schoolName = 'School Report Card') {
  const { student, exams } = reportData;

  const gradeLabel = (pct) => {
    if (pct >= 90) return 'A+';
    if (pct >= 75) return 'A';
    if (pct >= 60) return 'B';
    if (pct >= 45) return 'C';
    return 'D';
  };

  const gradeColor = (pct) => {
    if (pct >= 75) return [5, 150, 105];    // emerald
    if (pct >= 50) return [217, 119, 6];    // amber
    return [220, 38, 38];                   // red
  };

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210;
  let y = 0;

  // ── Header bar ──
  doc.setFillColor(13, 148, 136); // teal-600
  doc.rect(0, 0, W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(schoolName, W / 2, 10, { align: 'center' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Student Progress Report', W / 2, 17, { align: 'center' });

  y = 30;

  // ── Student info box ──
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(226, 232, 240);
  doc.roundedRect(14, y, W - 28, 18, 2, 2, 'FD');
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('STUDENT NAME', 20, y + 6);
  doc.text('ADMISSION NO.', 80, y + 6);
  doc.text('CLASS', 145, y + 6);
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(student.name || '-', 20, y + 13);
  doc.text(student.admission_number || '-', 80, y + 13);
  doc.text(`${student.class_name || ''} ${student.section || ''}`.trim() || '-', 145, y + 13);

  y += 26;

  // ── Exams ──
  exams.forEach((exam) => {
    // Check if we need a new page
    const examHeight = 14 + exam.subjects.length * 8 + 12;
    if (y + examHeight > 275) {
      doc.addPage();
      y = 15;
    }

    // Exam header
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(14, y, W - 28, 10, 1, 1, 'FD');
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(exam.exam_name, 20, y + 7);

    const pct = exam.overall_percentage;
    const [r, g, b] = gradeColor(pct);
    doc.setTextColor(r, g, b);
    doc.setFontSize(10);
    doc.text(`${gradeLabel(pct)}  ${pct}%  (${exam.total_obtained}/${exam.total_marks})`, W - 20, y + 7, { align: 'right' });

    y += 12;

    // Table header
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.rect(14, y, W - 28, 7, 'FD');
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.text('SUBJECT', 20, y + 5);
    doc.text('MAX', 115, y + 5, { align: 'center' });
    doc.text('OBTAINED', 140, y + 5, { align: 'center' });
    doc.text('%', 163, y + 5, { align: 'center' });
    doc.text('GRADE', 185, y + 5, { align: 'center' });

    y += 8;

    // Subject rows
    exam.subjects.forEach((sub, i) => {
      if (i % 2 === 0) {
        doc.setFillColor(252, 252, 253);
        doc.rect(14, y - 1, W - 28, 8, 'F');
      }
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(sub.subject, 20, y + 5);
      doc.text(String(sub.max_marks), 115, y + 5, { align: 'center' });
      doc.setFont('helvetica', 'bold');
      doc.text(String(sub.obtained), 140, y + 5, { align: 'center' });
      const [sr, sg, sb] = gradeColor(sub.percentage);
      doc.setTextColor(sr, sg, sb);
      doc.text(`${sub.percentage}%`, 163, y + 5, { align: 'center' });
      doc.text(gradeLabel(sub.percentage), 185, y + 5, { align: 'center' });
      doc.setDrawColor(241, 245, 249);
      doc.line(14, y + 7, W - 14, y + 7);
      y += 8;
    });

    // Total row
    doc.setFillColor(241, 245, 249);
    doc.rect(14, y - 1, W - 28, 8, 'F');
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('TOTAL', 20, y + 5);
    doc.text(String(exam.total_marks), 115, y + 5, { align: 'center' });
    doc.text(String(exam.total_obtained), 140, y + 5, { align: 'center' });
    const [tr, tg, tb] = gradeColor(exam.overall_percentage);
    doc.setTextColor(tr, tg, tb);
    doc.text(`${exam.overall_percentage}%`, 163, y + 5, { align: 'center' });
    doc.text(gradeLabel(exam.overall_percentage), 185, y + 5, { align: 'center' });

    y += 14;
  });

  // ── Footer ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(226, 232, 240);
    doc.line(14, 285, W - 14, 285);
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 290);
    doc.text('Powered by Ariskool', W - 14, 290, { align: 'right' });
    if (pageCount > 1) doc.text(`Page ${i} of ${pageCount}`, W / 2, 290, { align: 'center' });
  }

  // Auto-download — no dialog
  const filename = `ReportCard_${(student.name || 'Student').replace(/\s+/g, '_')}_${student.admission_number || ''}.pdf`;
  doc.save(filename);
}
